// These must be the first 2 lines in src/index.js
import { ScrollToTop, SnackbarMessage, TelemetryProvider } from "@knowfully/ui.common";
import App from "components/App";
import { AppDependenciesProvider } from "components/App/AppDependenciesProvider";
import useGlobalContext from "hooks/globalData";
import { useTelemetryConfig } from "hooks/useTelemetryConfig";
import Certificates from "pages/Certificates";
import ExamResultPage from "pages/Certificates/ExamResultPage";
import CourseCatalog from "pages/CourseCatalog";
import { CourseDetails } from "pages/CourseCatalog/CourseDetails";
import { CourseDetailsEnrollUsers } from "pages/CourseCatalog/CourseDetailsEnrollUsers";
import HomeStudyTab from "pages/CourseCatalog/HomeStudyTab";
import LiveOnlineTab from "pages/CourseCatalog/LiveOnlineTab";
import SeriesTab from "pages/CourseCatalog/SeriesTab";
import Enrollments from "pages/Enrollments";
import ErrorPage from "pages/InvalidStates/ErrorPage";
import { NotFoundPage } from "pages/InvalidStates/NotFoundPage";
import { UnauthorizedPage } from "pages/InvalidStates/UnauthorizedPage";
import LoginPage from "pages/Login";
import LoginComplete from "pages/LoginComplete";
import UsersPage from "pages/Users";
import { FC } from "react";
import ReactDOM from "react-dom/client";
import { QueryClient } from "react-query";
import { BrowserRouter, Route, RouteProps, Routes } from "react-router-dom";
import authStore from "stores/authStore";

const baseUrl = document
  .getElementsByTagName("base")[0]
  .getAttribute("href") as string | undefined;

const rootElement = document.getElementById("root")!;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      retryOnMount: false,
      refetchOnWindowFocus: false,
    },
  },
});

const LoggedInRequired: FC<RouteProps> = ({ children }) => {
  // Ensures that the user is logged in, and display LoginPage if they're not
  //   const isLoggedIn = authStore.useState((s) => s.isLoggedIn);
  const isLoggedIn = authStore.useState((s) => s.isLoggedIn);

  if (!isLoggedIn) {
    return <LoginPage />;
  }

  return <App />;
};

const AppRoutes = () => {
  const {
    loginCompleteUrl,
    defaultUrl,
    loginUrl,
    unauthorizedUrl,
    errorUrl,
    usersUrl,
    certificatesUrl,
    orgExamResultUrl,
    enrollmentsUrl,
    courseCatalogUrl,
    liveOnlineCatalogUrl,
    homeStudyCatalogUrl,
    seriesUrl,
    courseDetailsUrl,
    courseDetailsEnrollUsersUrl,
    seriesDetailsEnrollUsersUrl,
  } = useGlobalContext();

  return (
    <Routes>
      <Route path={loginCompleteUrl()} element={<LoginComplete />} />
      <Route path={loginUrl()} element={<LoginPage />} />
      <Route path={defaultUrl()} element={<LoggedInRequired />}>
        <Route path={courseCatalogUrl()} element={<CourseCatalog />} />
        <Route path={usersUrl()} element={<UsersPage />} />
        <Route path={certificatesUrl()} element={<Certificates />} />
        <Route path={orgExamResultUrl()} element={<ExamResultPage />} />
        <Route path={enrollmentsUrl()} element={<Enrollments />} />
        <Route path={liveOnlineCatalogUrl()} element={<LiveOnlineTab />} />
        <Route path={homeStudyCatalogUrl()} element={<HomeStudyTab />} />
        <Route path={seriesUrl()} element={<SeriesTab />} />
        <Route path={unauthorizedUrl()} element={<UnauthorizedPage />} />
        <Route path={errorUrl()} element={<ErrorPage />} />
        <Route path={courseDetailsUrl()} element={<CourseDetails />} />
        <Route
          path={courseDetailsEnrollUsersUrl()}
          element={<CourseDetailsEnrollUsers />}
        />
        <Route
          path={seriesDetailsEnrollUsersUrl()}
          element={<CourseDetailsEnrollUsers isSeries={true} />}
        />
        <Route element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
};

const AppWithTelemetry = () => {
  const config = useTelemetryConfig();
  return (
    <TelemetryProvider config={config} ErrorComponent={ErrorPage}>
      <AppDependenciesProvider>
        <AppRoutes />
        <SnackbarMessage />
      </AppDependenciesProvider>
    </TelemetryProvider>
  );
};

const root = ReactDOM.createRoot(rootElement);
root.render(
  <BrowserRouter basename={baseUrl}>
    <ScrollToTop />
    <AppWithTelemetry />
  </BrowserRouter>
);
